import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineView } from "foldkit/submodel";

import {
	type AttributeSchemaEntry,
	EntityType,
	ValueType,
	normalizeEntityType,
} from "../../api/schemas/attributeSchema";
import { busyAttributes, busySpinner } from "../../widgets/busy";
import type { Shared } from "../shared";
import { Message } from "./message";
import type { Form, Model } from "./model";

const VALUE_TYPES = ValueType.literals;

/** v1's strings on this page are untranslated literals; kept verbatim (R13). */

const field = (
	h: HtmlBuilder<Message>,
	id: string,
	label: string,
	control: Html,
): Html =>
	h.div(
		[],
		[h.label([h.Class("label label-text"), h.For(id)], [label]), control],
	);

const textInput = (
	h: HtmlBuilder<Message>,
	id: string,
	value: string,
	placeholder: string,
	onInput: (value: string) => Message,
	disabled = false,
): Html =>
	h.input([
		h.Id(id),
		h.Type("text"),
		h.Class("input input-bordered input-sm w-full"),
		h.Value(value),
		h.Placeholder(placeholder),
		h.Disabled(disabled),
		h.OnChange(onInput),
	]);

const form = (h: HtmlBuilder<Message>, model: Model, f: Form): Html => {
	const editing = Option.isSome(f.editingId);
	return h.div(
		[h.Class("card bg-base-100 border border-base-300 shadow-sm mb-6")],
		[
			h.div(
				[h.Class("card-body p-4")],
				[
					h.h2(
						[h.Class("font-semibold mb-3")],
						[editing ? "Edit Schema Entry" : "New Schema Entry"],
					),
					Option.match(f.error, {
						onNone: () => h.empty,
						onSome: (error) =>
							h.div(
								[h.Role("alert"), h.Class("alert alert-error alert-sm mb-3")],
								[h.span([], [error])],
							),
					}),
					h.form(
						[h.Class("space-y-3"), h.OnSubmit(Message.SubmittedForm())],
						[
							h.div(
								[h.Class("grid grid-cols-1 md:grid-cols-2 gap-3")],
								[
									field(
										h,
										"schema-entity-type",
										"Entity Type",
										h.select(
											[
												h.Id("schema-entity-type"),
												h.Class("select select-bordered select-sm w-full"),
												h.Value(f.entityType),
												h.Disabled(editing),
												h.OnChange((value) =>
													Message.ChangedEntityType({
														value: value === "entry" ? "entry" : "record",
													}),
												),
											],
											EntityType.literals.map((value) =>
												h.option(
													[h.Value(value), h.Selected(f.entityType === value)],
													[value === "record" ? "Record" : "Entry"],
												),
											),
										),
									),
									field(
										h,
										"schema-key",
										"Key",
										textInput(
											h,
											"schema-key",
											f.key.value,
											"e.g. birthday, company, priority",
											(value) => Message.ChangedKey({ value }),
											editing,
										),
									),
									field(
										h,
										"schema-value-type",
										"Value Type",
										h.select(
											[
												h.Id("schema-value-type"),
												h.Class("select select-bordered select-sm w-full"),
												h.Value(f.valueType),
												h.OnChange((value) =>
													Message.ChangedValueType({
														value:
															VALUE_TYPES.find((v) => v === value) ?? "string",
													}),
												),
											],
											VALUE_TYPES.map((value) =>
												h.option(
													[h.Value(value), h.Selected(f.valueType === value)],
													[value],
												),
											),
										),
									),
									field(
										h,
										"schema-label",
										"Label (optional)",
										textInput(
											h,
											"schema-label",
											f.label,
											"Display label",
											(value) => Message.ChangedLabel({ value }),
										),
									),
									field(
										h,
										"schema-subtype",
										"Sub Type (optional)",
										textInput(
											h,
											"schema-subtype",
											f.subType,
											"e.g. person, group",
											(value) => Message.ChangedSubType({ value }),
											editing,
										),
									),
									field(
										h,
										"schema-sort-order",
										"Sort Order",
										h.input([
											h.Id("schema-sort-order"),
											h.Type("number"),
											h.Class("input input-bordered input-sm w-full"),
											h.Value(String(f.sortOrder)),
											h.Min("0"),
											h.Max("9999"),
											h.OnChange((value) =>
												Message.ChangedSortOrder({ value }),
											),
										]),
									),
								],
							),
							field(
								h,
								"schema-description",
								"Description (optional)",
								h.textarea([
									h.Id("schema-description"),
									h.Class("textarea textarea-bordered textarea-sm w-full"),
									h.Rows(2),
									h.Value(f.description),
									h.Placeholder("Describe what this attribute is used for"),
									h.OnChange((value) => Message.ChangedDescription({ value })),
								]),
							),
							h.div(
								[h.Class("flex gap-2 justify-end")],
								[
									h.button(
										[
											h.Type("button"),
											h.Class("btn btn-ghost btn-sm"),
											h.AriaLabel("Cancel"),
											h.OnClick(Message.ClickedCancel()),
										],
										["Cancel"],
									),
									h.button(
										[
											h.Type("submit"),
											h.Class("btn btn-primary btn-sm"),
											h.AriaLabel(editing ? "Update" : "Create"),
											...busyAttributes(h, model.saving),
										],
										[
											busySpinner(h, model.saving),
											editing ? "Update" : "Create",
										],
									),
								],
							),
						],
					),
				],
			),
		],
	);
};

const entryTable = (
	h: HtmlBuilder<Message>,
	title: string,
	list: ReadonlyArray<AttributeSchemaEntry>,
): Html =>
	h.div(
		[h.Class("mb-8")],
		[
			h.h3([h.Class("text-lg font-semibold mb-3")], [title]),
			list.length === 0
				? h.p(
						[h.Class("text-base-content/60 text-sm")],
						["No schema entries defined yet."],
					)
				: h.div(
						[h.Class("overflow-x-auto")],
						[
							h.table(
								[h.Class("table table-sm"), h.AriaLabel(title)],
								[
									h.thead(
										[],
										[
											h.tr(
												[],
												[
													"Key",
													"Type",
													"Label",
													"Description",
													"Order",
													"Actions",
												].map((c) => h.th([], [c])),
											),
										],
									),
									h.tbody(
										[],
										[...list]
											.sort((a, b) => a.sortOrder - b.sortOrder)
											.map((entry) =>
												h.tr(
													[h.Key(entry.id)],
													[
														h.td([h.Class("font-mono text-sm")], [entry.key]),
														h.td(
															[],
															[
																h.span(
																	[h.Class("badge badge-outline badge-sm")],
																	[entry.valueType],
																),
															],
														),
														h.td([], [entry.label ?? "-"]),
														h.td(
															[h.Class("max-w-xs truncate")],
															[entry.description ?? "-"],
														),
														h.td([], [String(entry.sortOrder)]),
														h.td(
															[h.Class("flex gap-1")],
															[
																h.button(
																	[
																		h.Type("button"),
																		h.Class("btn btn-ghost btn-xs"),
																		h.AriaLabel(
																			`Edit schema entry ${entry.key}`,
																		),
																		h.OnClick(
																			Message.ClickedEdit({ id: entry.id }),
																		),
																	],
																	["✎"],
																),
																h.button(
																	[
																		h.Type("button"),
																		h.Class("btn btn-ghost btn-xs text-error"),
																		h.AriaLabel(
																			`Delete schema entry ${entry.key}`,
																		),
																		h.OnClick(
																			Message.ClickedDelete({ id: entry.id }),
																		),
																	],
																	["🗑"],
																),
															],
														),
													],
												),
											),
									),
								],
							),
						],
					),
		],
	);

export const view = defineView<Model, Message, Shared>((model, _shared, h) => {
	if (AsyncData.isFailure(model.schema)) {
		return h.div(
			[
				h.DataAttribute("testid", "page-AttributeSchemaSettings"),
				h.Role("alert"),
				h.Class("alert alert-error"),
			],
			[h.span([], ["Failed to load attribute schema"])],
		);
	}
	const entries = AsyncData.getOrElse(model.schema, () => ({ data: [] })).data;
	return h.div(
		[
			h.DataAttribute("testid", "page-AttributeSchemaSettings"),
			h.Class("max-w-4xl mx-auto px-4 py-8"),
		],
		[
			h.div(
				[h.Class("flex items-center justify-between mb-6")],
				[
					h.h1([h.Class("text-2xl font-bold")], ["Attribute Schema"]),
					Option.isSome(model.form)
						? h.empty
						: h.button(
								[
									h.Type("button"),
									h.Class("btn btn-primary btn-sm"),
									h.AriaLabel("Add schema entry"),
									h.OnClick(Message.ClickedAddEntry()),
								],
								["Add Entry"],
							),
				],
			),
			h.p(
				[h.Class("text-base-content/70 mb-6")],
				[
					"Define suggested attributes for records and entries. These appear as pre-populated fields in the attribute editor but do not enforce types.",
				],
			),
			Option.match(model.form, {
				onNone: () => h.empty,
				onSome: (f) => form(h, model, f),
			}),
			AsyncData.isLoading(model.schema)
				? h.div(
						[h.Role("status"), h.Class("w-full text-center")],
						[h.span([h.Class("loading loading-dots loading-lg")], [])],
					)
				: h.div(
						[],
						[
							entryTable(
								h,
								"Record Attributes",
								entries.filter(
									(e) => normalizeEntityType(e.entityType) === "record",
								),
							),
							entryTable(
								h,
								"Entry Attributes",
								entries.filter(
									(e) => normalizeEntityType(e.entityType) === "entry",
								),
							),
						],
					),
		],
	);
});
